/**
 * HighlightedText — renders extracted document text with an optional
 * `[start, end)` character-range highlight, auto-scrolling the highlighted
 * passage into view.
 *
 * Shared by:
 *   - the standalone `/docs/:docId` route page (`pages/DocViewer.tsx`,
 *     R-70 char-range highlight), and
 *   - the in-context `ExtractedTextDialog` (H-4 passage-offset citation
 *     deep-links: a `[Ref N]` whose `LinkedDocumentRef.sourceOffset`
 *     matched a verbatim excerpt opens the extracted-text view scrolled
 *     to / highlighting the exact passage).
 *
 * One renderer keeps the two surfaces byte-identical so a passage looks
 * the same whether reached via the route or the modal. When the span is
 * empty / out of range the component degrades to the plain `<pre>` (no
 * highlight), so absent-offset callers render exactly as before.
 *
 * The `<mark>` colour mirrors the long-standing route-page highlight
 * (`bg-amber-200/80`) for visual continuity — amber is the established
 * "this is the cited passage" affordance.
 */
import React from 'react';

export interface HighlightedTextProps {
  /** The document's extracted text (`SiteDocument.textContent`). */
  text: string;
  /** 0-based inclusive start of the highlight span. */
  highlightStart: number;
  /** 0-based exclusive end of the highlight span (half-open). */
  highlightEnd: number;
  /** Optional className override for the <pre>. */
  className?: string;
  /** Optional data-testid for the <pre> (defaults to `doc-viewer-text`). */
  testId?: string;
}

export function HighlightedText({
  text,
  highlightStart,
  highlightEnd,
  className = 'text-xs p-4 whitespace-pre-wrap font-mono',
  testId = 'doc-viewer-text',
}: HighlightedTextProps): React.ReactElement {
  const markRef = React.useRef<HTMLElement | null>(null);
  const hasHighlight = highlightEnd > highlightStart && highlightStart >= 0;

  // Scroll the highlighted passage into view once it mounts / changes. No-op
  // when there's no highlight. `block: 'center'` mirrors the appendix
  // scroll-into-view affordance in RefLink so the passage lands centred.
  React.useEffect(() => {
    if (!hasHighlight || !markRef.current) return;
    if (typeof markRef.current.scrollIntoView === 'function') {
      markRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [hasHighlight, highlightStart, highlightEnd, text]);

  if (!hasHighlight) {
    return (
      <pre className={className} data-testid={testId}>
        {text}
      </pre>
    );
  }

  const start = Math.max(0, Math.min(highlightStart, text.length));
  const end = Math.max(start, Math.min(highlightEnd, text.length));
  return (
    <pre className={className} data-testid={testId}>
      {text.slice(0, start)}
      <mark
        ref={markRef}
        className="bg-amber-200/80 text-inherit rounded-sm"
        data-testid="doc-viewer-highlight"
      >
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </pre>
  );
}
